import type { dash } from "@dropbox/api-v2-client";
import { UIIcon } from "@dropbox/dig-icons";
import { Twinkle2Line } from "@dropbox/dig-icons/assets";
import { useFlyoutPanel } from "@mirage/mosaics/FlyoutPanel/useFlyoutPanel";
import { ResultAction } from "@mirage/mosaics/Results/ResultActions";
import type { ChatEntryPoint } from "@mirage/mosaics/SummaryQnaPanel/SummaryQna";
import { DisplayActionTypes } from "@mirage/search/SearchResults/ResultRow/types";
import { useShouldSummarizeNavigateToChat } from "@mirage/service-conversation/hooks/useShouldSummarizeNavigateToChat";
import type { SummarizeItemParams } from "@mirage/service-conversation/utils/buildConversationRouterState";
import {
  buildConversationRouterState,
  CtaSource,
} from "@mirage/service-conversation/utils/buildConversationRouterState";
import { RoutePath } from "@mirage/webapp/routeTypes";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { NormalizeResultInputType, SummaryQnAEnum } from "./utils";
import { normalizeResultForSummary, useSummarizableReasons } from "./utils";

export const SummarizeAction = ({
  result,
  summaryQnAType,
  entrypoint,
  resultPosition,
  resultPositionNoCta,
  summarizable,
}: {
  result?: NormalizeResultInputType;
  summaryQnAType: SummaryQnAEnum;
  entrypoint: ChatEntryPoint;
  resultPosition?: number;
  resultPositionNoCta?: number;
  summarizable?: dash.SummarizableType[".tag"];
}) => {
  const navigate = useNavigate();
  const { summarize } = useFlyoutPanel();
  const shouldSummarizeNavigateToChat = useShouldSummarizeNavigateToChat();
  const getSummarizableReason = useSummarizableReasons();
  const notSummarizable =
    typeof result === "object" && summarizable !== "yes_summarizable";

  const handleSummarize = useCallback(() => {
    if (!result) {
      return;
    }
    if (
      shouldSummarizeNavigateToChat &&
      (entrypoint === "activity_feed_summary" ||
        entrypoint === "recent_summary")
    ) {
      const summarizableResult = normalizeResultForSummary(
        result,
        summaryQnAType,
      );
      if (!summarizableResult) {
        return;
      }
      const summarizeItemParams: SummarizeItemParams = {
        chatSources: [{ type: "summarizable_result", summarizableResult }],
        scenarioMeta: { type: "feed" },
      };
      const ctaSource =
        entrypoint === "activity_feed_summary"
          ? CtaSource.FeedItemSummarize
          : CtaSource.RecentSummarize;
      const routerState = buildConversationRouterState(
        ctaSource,
        summarizeItemParams,
      );
      navigate(RoutePath.CHAT, { state: routerState });
    }
    summarize(
      result,
      summaryQnAType,
      entrypoint,
      resultPosition,
      resultPositionNoCta,
    );
  }, [
    entrypoint,
    navigate,
    result,
    resultPosition,
    resultPositionNoCta,
    shouldSummarizeNavigateToChat,
    summarize,
    summaryQnAType,
  ]);

  return (
    <ResultAction
      action={{
        key: "summarize",
        title: getSummarizableReason(summarizable),
        type: DisplayActionTypes.IconButton,
        icon: <UIIcon src={Twinkle2Line} />,
        disabled: notSummarizable,
        onClick: handleSummarize,
      }}
    />
  );
};
